import { type FC } from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/flexkit/Collapsible";
import type { AgentMessageFragmentTodoItem } from "@/hooks/use-stream-conversation";
import { cn } from "@/components/podkit/lib/cn";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import { AgentResponseBlock_TodoItem_Phase } from "gitpod-next-api/gitpod/v1/agent_pb";
import { useTodoViewPreferences } from "@/hooks/use-todo-view-preferences";
import { TodoListItem } from "@/components/ai/chat/TodoListItem";
import { TodoListHeader, StopButton } from "@/components/ai/chat/TodoListHeader";

export type TodoItem = Pick<AgentMessageFragmentTodoItem, "todoItemId" | "title" | "status">;

export const TodoList: FC<{
    todos: TodoItem[];
    agentExecution: PlainAgentExecution;
}> = ({ todos, agentExecution }) => {
    const { isOpen, setIsOpen } = useTodoViewPreferences();

    const visibleTodos = todos.filter((todo) => todo.status !== AgentResponseBlock_TodoItem_Phase.CLEARED);
    const hasTodos = visibleTodos.length > 0;
    return (
        <div className="rounded-t-lg">
            <Collapsible open={isOpen} onOpenChange={setIsOpen} disabled={!hasTodos}>
                <div className="flex w-full items-center justify-between gap-2 rounded-t-xl px-4 py-1.5">
                    <CollapsibleTrigger
                        className={cn(
                            "flex min-w-0 flex-1 items-center gap-2",
                            hasTodos ? "cursor-pointer" : "cursor-default",
                        )}
                        aria-label={hasTodos ? "Toggle todo list" : "Conversation status"}
                    >
                        <TodoListHeader todos={visibleTodos} agentExecution={agentExecution} isOpen={isOpen} />
                    </CollapsibleTrigger>
                    <StopButton agentExecution={agentExecution} />
                </div>
                <CollapsibleContent>
                    {hasTodos && (
                        <div
                            className="mx-[14px] max-h-80 overflow-y-auto rounded-t-lg bg-surface-pure p-1"
                            role="list"
                            aria-label="Todo items list"
                        >
                            {visibleTodos.map((todo, index) => (
                                <TodoListItem key={todo.todoItemId} todo={todo} index={index} />
                            ))}
                        </div>
                    )}
                </CollapsibleContent>
            </Collapsible>
        </div>
    );
};
